import classNames from "classnames";
import { Collapsible } from "@base-ui/react/collapsible";
import { ChevronRightSmallIcon } from "@icons";
import { useSideNavMode } from "./SideNavProvider";
import { SideNavRowActive, SideNavRowTarget } from "./SideNavRowTarget";
import type { SideNavItemProps } from "./SideNav.types";
import styles from "./SideNavItem.module.scss";

export type { SideNavItemProps } from "./SideNav.types";

/**
 * A 28px nav row (Figma `SideNavItem`): `to` for an in-app route, `href` for
 * an external page, or `onClick` for an action. With `children` its icon is
 * also a toggle, swapped for a chevron on hover, that expands it into
 * `SideNavSubItem` rows. In a mini nav it is its icon and a tooltip: no
 * toggle, trailing slots or sub-rows, `isExpanded` is kept for open, and the
 * row is also active for `isSubItemActive`.
 */
export const SideNavItem = (props: SideNavItemProps) => {
    const {
        icon,
        label,
        labelCopyId,
        labelCopyLabel,
        shortcut,
        trailing,
        trailingVisibility = "always",
        hoverAction,
        isActive: isActiveOverride,
        isExpanded = false,
        onExpandedChange,
        isSubItemActive = false,
        children,
        className,
        ref,
        "data-forced-state": forcedState,
    } = props;

    const isMini = useSideNavMode() === "mini";
    const isExpandable = onExpandedChange !== undefined;
    const hasToggle = isExpandable && !isMini;
    const isActiveForSubItem = isMini && isSubItemActive;
    const iconSlot = <span className={styles.icon}>{icon}</span>;

    const renderRow = (isActive: boolean) => (
        <div
            ref={ref}
            className={classNames(styles.item, className)}
            data-active={isActive || undefined}
            data-forced-state={forcedState}
        >
            {hasToggle && (
                <Collapsible.Trigger
                    className={styles.toggle}
                    aria-label={`${label} pages`}
                >
                    {iconSlot}
                    <span className={styles.chevron} aria-hidden>
                        <ChevronRightSmallIcon />
                    </span>
                </Collapsible.Trigger>
            )}
            <SideNavRowTarget
                target={props}
                isActive={isActive}
                label={label}
                labelCopyId={labelCopyId}
                labelCopyLabel={labelCopyLabel}
                shortcut={shortcut}
                leading={hasToggle ? undefined : iconSlot}
                className={styles.target}
                labelClassName={styles.label}
            />

            {!isMini && trailing != null && (
                <span
                    className={styles.trailing}
                    data-visibility={trailingVisibility}
                >
                    {typeof trailing === "function"
                        ? trailing({ isActive })
                        : trailing}
                </span>
            )}
            {!isMini && hoverAction != null && (
                <span className={styles.hoverAction}>{hoverAction}</span>
            )}
        </div>
    );

    return (
        <SideNavRowActive
            target={props}
            isActive={isActiveOverride ?? (isActiveForSubItem || undefined)}
        >
            {(isActive) =>
                onExpandedChange === undefined ? (
                    renderRow(isActive)
                ) : (
                    <Collapsible.Root
                        open={isExpanded && !isMini}
                        onOpenChange={(isOpen) => {
                            if (!isMini) {
                                onExpandedChange(isOpen);
                            }
                        }}
                    >
                        {renderRow(isActive)}
                        <Collapsible.Panel
                            className={styles.subItemsPanel}
                            keepMounted
                        >
                            <div className={styles.subItems}>{children}</div>
                        </Collapsible.Panel>
                    </Collapsible.Root>
                )
            }
        </SideNavRowActive>
    );
};
